import { useState, useEffect, useRef, useCallback } from "react";
import {getImproveAPI, UserControllerGetFeedParams, UserControllerGetUpcomingLessonsParams, GetFeedResponse, GetEnrolledCoursesResponse, UserControllerGetEnrolledCoursesParams, UserControllerGetOverdueLessonsParams, UserControllerGetRecentActivityParams, UserControllerGetActiveCoursesParams, UserControllerGetFinishedCoursesParams, UpdateSettingsBody, UpdateSettingsResponse, EnsureAccountBody} from "../gen";

import {useImproveAxios} from "../../hooks/useImproveAxios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Query key constants for cache invalidation
 *
 * Export these so mutations can invalidate the correct queries.
 * Using constants ensures consistency across the app.
 */
export const QUERY_KEYS = {
	ENSURE_ACCOUNT: "ensureAccount",
	WHOAMI: "whoami",
	ME: "me",
	FEED: "feed",
	UPCOMING_LESSONS: "upcomingLessons",
	ENROLLED_COURSES: "enrolledCourses",
	RECENT_ACTIVITY: "recentActivity",
	ACTIVE_COURSES: "activeCourses",
	FINISHED_COURSES: "finishedCourses"
} as const;

/**
 * Shared React Query hooks for user-related data
 *
 * Consolidated user functionality including:
 * - Authenticated user info (whoami)
 * - User profile (me)
 * - User feed (with stale-while-revalidate caching)
 * - Enrolled courses
 *
 * All hooks are generic and reusable, providing caching and refetching.
 *
 * ## Caching Strategy
 *
 * Feed uses a 10-minute stale time with stale-while-revalidate:
 * - Fresh data is returned immediately from cache
 * - After 10 minutes, data is "stale" but still returned
 * - A background refetch updates the cache
 *
 * Cache is invalidated immediately on:
 * - Lesson completion (via useCompleteLesson mutation)
 * - New course enrollments (via enrollment events)
 */

const ENSURE_ACCOUNT_POLL_INTERVAL_MS = 2500;
const ENSURE_ACCOUNT_MAX_POLL_ATTEMPTS = 60; // ~2.5 min max

/**
 * Ensure account exists and optionally poll get-me until user is ready.
 *
 * - 200 from ensure-account: user exists, ready immediately
 * - 202 from ensure-account: user creation enqueued, polls get-me until success
 *
 * Uses React Query's refetchInterval for polling (poll only when needToPoll).
 */
export const useEnsureAccountQuery = (options?: { enabled?: boolean }) => {
	const axiosConfig = useImproveAxios();
	const { userControllerEnsureAccount, userControllerGetMe } = getImproveAPI();

	const ensureEnabled = options?.enabled !== false;

	const ensureAccountQuery = useQuery({
		queryKey: [QUERY_KEYS.ENSURE_ACCOUNT, axiosConfig.baseURL],
		queryFn: async () => {
			const body: EnsureAccountBody = {
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			};
			const response = await userControllerEnsureAccount(body, axiosConfig);
			const data = response.data as unknown as { status?: string } | undefined;
			const status = data?.status ?? (response.status === 200 ? "ready" : "accepted");
			return { status: status as "ready" | "accepted" };
		},
		enabled: ensureEnabled,
		staleTime: Infinity, // Once ready, never refetch
		retry: false,
	});

	const needToPoll = ensureAccountQuery.data?.status === "accepted";
	const [gaveUpPolling, setGaveUpPolling] = useState(false);

	useEffect(() => {
		if (!needToPoll) {
			setGaveUpPolling(false);
			return;
		}
		const timer = setTimeout(
			() => setGaveUpPolling(true),
			ENSURE_ACCOUNT_POLL_INTERVAL_MS * ENSURE_ACCOUNT_MAX_POLL_ATTEMPTS
		);
		return () => clearTimeout(timer);
	}, [needToPoll]);

	const meQuery = useQuery({
		queryKey: [QUERY_KEYS.ME],
		queryFn: async () => {
			const response = await userControllerGetMe(axiosConfig);
			return response.data;
		},
		enabled: needToPoll,
		refetchInterval: needToPoll ? ENSURE_ACCOUNT_POLL_INTERVAL_MS : false,
		refetchIntervalInBackground: false,
		retry: false,
		staleTime: 10 * 60 * 1000,
	});

	const isReady =
		(ensureAccountQuery.data?.status === "ready") ||
		(needToPoll && meQuery.isSuccess && !!meQuery.data) ||
		ensureAccountQuery.isError ||
		(needToPoll && meQuery.isError) ||
		gaveUpPolling;

	const status: "idle" | "checking" | "ready" | "polling" | "error" =
		ensureAccountQuery.isError || gaveUpPolling || (needToPoll && meQuery.isError)
			? "error"
			: ensureAccountQuery.isLoading
				? "checking"
				: ensureAccountQuery.data?.status === "ready"
					? "ready"
					: needToPoll
						? meQuery.isSuccess
							? "ready"
							: "polling"
						: "checking";

	return {
		isReady,
		status,
		isLoading: !isReady,
		error: ensureAccountQuery.error ?? meQuery.error,
	};
};

/**
 * Get current authenticated user info
 */
export const useWhoAmI = (options?: { enabled?: boolean }) => {
	const axiosConfig = useImproveAxios();
	const {userControllerWhoAmI} = getImproveAPI();

	return useQuery({
		queryKey: [QUERY_KEYS.WHOAMI],
		queryFn: async () => {
			const response = await userControllerWhoAmI(axiosConfig);
			return response.data;
		},
		staleTime: 30 * 60 * 1000, // 30 minutes - auth info doesn't change often
		enabled: options?.enabled !== false
	});
};

/**
 * Get current user profile data
 *
 * Invalidated on lesson completion ({@link useCompleteLesson}) so fields like last completed lesson stay fresh.
 */
export const useGetMe = (options?: { enabled?: boolean }) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetMe} = getImproveAPI();

	return useQuery({
		queryKey: [QUERY_KEYS.ME],
		queryFn: async () => {
			const response = await userControllerGetMe(axiosConfig);
			return response.data;
		},
		staleTime: 10 * 60 * 1000, // 10 minutes - user data doesn't change often
		enabled: options?.enabled !== false
	});
};

/**
 * Get user's personalized training feed
 *
 * Uses a 10-minute stale time with stale-while-revalidate strategy:
 * - Returns cached data immediately (great UX)
 * - Background refetch after 10 minutes of staleness
 * - Cache is explicitly invalidated on lesson completion
 *
 * @param parameters - Feed query parameters (startDate, timezone)
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useGetFeed({
 *   startDate: new Date().toISOString(),
 *   timezone: 'America/New_York'
 * });
 * ```
 */
export const useGetFeed = (
	parameters: UserControllerGetFeedParams,
	options?: { enabled?: boolean }
) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetFeed} = getImproveAPI();

	return useQuery<GetFeedResponse>({
		queryKey: [QUERY_KEYS.FEED, parameters],
		queryFn: async () => {
			const response = await userControllerGetFeed(parameters, axiosConfig);
			return response.data;
		},
		enabled: options?.enabled !== false,
		// 10 minutes - feed is computed server-side, cache aggressively
		// Invalidated explicitly on lesson completion via useCompleteLesson
		staleTime: 10 * 60 * 1000
	});
};

/**
 * Get user's enrolled courses with progress
 *
 * Uses a 5-minute stale time. Invalidated on lesson completion
 * to reflect updated progress percentages.
 *
 * @param parameters - Optional filters (includeCompleted, includeSoftDeleted, locale)
 */
export const useGetEnrolledCourses = (parameters?: UserControllerGetEnrolledCoursesParams) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetEnrolledCourses} = getImproveAPI();

	return useQuery<GetEnrolledCoursesResponse>({
		queryKey: [QUERY_KEYS.ENROLLED_COURSES, parameters],
		queryFn: async () => {
			const response = await userControllerGetEnrolledCourses(parameters, axiosConfig);
			return response.data;
		},
		staleTime: 5 * 60 * 1000 // 5 minutes
	});
};

/**
 * Get user's overdue lessons
 *
 * Uses a 10-minute stale time with stale-while-revalidate strategy:
 * - Returns cached data immediately (great UX)
 * - Background refetch after 10 minutes of staleness
 *
 * @param parameters - Required date and optional timezone
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useGetOverdueLessons({
 *   date: new Date().toISOString(),
 *   timezone: 'America/New_York'
 * });
 * ```
 */
export const useGetOverdueLessons = (parameters: UserControllerGetOverdueLessonsParams) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetOverdueLessons} = getImproveAPI();

	return useQuery<GetFeedResponse>({
		queryKey: ["overdueLessons", parameters],
		queryFn: async () => {
			const response = await userControllerGetOverdueLessons(parameters, axiosConfig);
			return response.data;
		},
		staleTime: 10 * 60 * 1000 // 10 minutes - same as feed
	});
};

/**
 * Get upcoming lessons only (no overdues), with optional limit.
 *
 * @param parameters - startDate, timezone, and optional limit
 */
export const useGetUpcomingLessons = (parameters: UserControllerGetUpcomingLessonsParams) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetUpcomingLessons} = getImproveAPI();

	return useQuery<GetFeedResponse>({
		queryKey: [QUERY_KEYS.UPCOMING_LESSONS, parameters],
		queryFn: async () => {
			const response = await userControllerGetUpcomingLessons(parameters, axiosConfig);
			return response.data;
		},
		staleTime: 10 * 60 * 1000 // 10 minutes
	});
};

/**
 * Request auto-enrollment for the current user.
 * If their cohort has training_feed_auto_assignment enabled, a background job
 * will enroll them in recommended courses. Rate limited: 1 request per minute.
 *
 * Use in empty training feed state to ensure new users get enrolled.
 */
export const useRequestAutoEnrollment = () => {
	const axiosConfig = useImproveAxios();
	const { userControllerRequestAutoEnrollment } = getImproveAPI();

	return useMutation<void, Error, void>({
		mutationFn: async () => {
			const response = await userControllerRequestAutoEnrollment(axiosConfig);
			return response.data;
		},
		// WebSocketProvider invalidates feed + enrolled-courses on enrollment/unenrollment completion events
	});
};

export type UseRefreshStatsOptions = {
	/**
	 * Minimum milliseconds between API calls (client-side throttle).
	 * Matches server rate limit (e.g. 60_000) to avoid redundant 429s.
	 * Mutations do not support staleTime; use this for "don't fire again yet" behavior.
	 */
	cooldownMs?: number;
};

/**
 * Enqueue refresh-stats on the server (202). Optional `cooldownMs` throttles repeated
 * `mutate` / `mutateAsync` calls; React Query mutations have no built-in staleTime.
 */
export const useRefreshStats = (options?: UseRefreshStatsOptions) => {
	const axiosConfig = useImproveAxios();
	const { userControllerRefreshStats } = getImproveAPI();
	const cooldownMs = options?.cooldownMs ?? 0;
	const lastInvokeRef = useRef<number>(0);

	const mutation = useMutation<void, Error, void>({
		mutationFn: async () => {
			const response = await userControllerRefreshStats(axiosConfig);
			return response.data;
		},
	});

	const mutate = useCallback(
		(...args: Parameters<typeof mutation.mutate>) => {
			const now = Date.now();
			if (cooldownMs > 0 && now - lastInvokeRef.current < cooldownMs) {
				return;
			}
			lastInvokeRef.current = now;
			mutation.mutate(...args);
		},
		[cooldownMs, mutation],
	);

	const mutateAsync = useCallback(
		async (...args: Parameters<typeof mutation.mutateAsync>) => {
			const now = Date.now();
			if (cooldownMs > 0 && now - lastInvokeRef.current < cooldownMs) {
				return;
			}
			lastInvokeRef.current = now;
			return mutation.mutateAsync(...args);
		},
		[cooldownMs, mutation],
	);

	return { ...mutation, mutate, mutateAsync };
};

/**
 * Update user settings (language, notifications, reminders)
 *
 * On success, invalidates the ME cache to refetch updated settings.
 *
 * @example
 * ```typescript
 * const { mutate: updateSettings, isPending } = useUpdateSettings();
 * 
 * updateSettings({
 *   preferredLanguage: 'es-419',
 *   notifyInApp: true,
 *   dailySkillsReminder: true
 * });
 * ```
 */
export const useUpdateSettings = () => {
	const axiosConfig = useImproveAxios();
	const {userControllerUpdateSettings} = getImproveAPI();
	const queryClient = useQueryClient();

	return useMutation<UpdateSettingsResponse, Error, UpdateSettingsBody>({
		mutationFn: async (settings: UpdateSettingsBody) => {
			const response = await userControllerUpdateSettings(settings, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			// Invalidate ME cache to refetch with updated settings
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ME]});
		}
	});
};

/**
 * Get current user's recent activity (completed lessons)
 * 
 * Returns paginated list of recently completed lessons with course context.
 * 
 * @param params - Optional pagination params (page, pageSize)
 * 
 * @example
 * ```typescript
 * const { data, isLoading } = useGetMyRecentActivity({
 *   page: 1,
 *   pageSize: 10
 * });
 * ```
 */
const DEFAULT_LOCALE = "en" as const;

export const useGetMyRecentActivity = (params?: {
	page?: number;
	pageSize?: number;
	locale?: string;
}) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetRecentActivity} = getImproveAPI();
	const effectiveParams: UserControllerGetRecentActivityParams = {
		...params,
		locale: (params?.locale ?? DEFAULT_LOCALE) as UserControllerGetRecentActivityParams["locale"],
	};

	return useQuery({
		queryKey: [QUERY_KEYS.RECENT_ACTIVITY, effectiveParams],
		queryFn: async () => {
			const response = await userControllerGetRecentActivity(effectiveParams, axiosConfig);
			return response.data;
		},
		staleTime: 2 * 60 * 1000 // 2 minutes - activity changes frequently
	});
};

/**
 * Get current user's active (in-progress) courses
 * 
 * Returns list of active courses with progress percentage.
 * Invalidated when enrollments or in-course progress change (WebSocket enroll/unenroll,
 * self-enroll, playlist-driven course enrollments, lesson completion, quiz completion when a course finishes).
 * 
 * @example
 * ```typescript
 * const { data, isLoading } = useGetMyActiveCourses();
 * ```
 */
export const useGetMyActiveCourses = (params?: { locale?: string }) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetActiveCourses} = getImproveAPI();
	const effectiveParams: UserControllerGetActiveCoursesParams = {
		locale: (params?.locale ?? DEFAULT_LOCALE) as UserControllerGetActiveCoursesParams["locale"],
	};

	return useQuery({
		queryKey: [QUERY_KEYS.ACTIVE_COURSES, effectiveParams],
		queryFn: async () => {
			const response = await userControllerGetActiveCourses(effectiveParams, axiosConfig);
			return response.data;
		},
		staleTime: 5 * 60 * 1000 // 5 minutes - course list doesn't change often
	});
};

/**
 * Get current user's finished (completed) courses
 * 
 * Returns list of completed courses with completion date.
 * 
 * @example
 * ```typescript
 * const { data, isLoading } = useGetMyFinishedCourses();
 * ```
 */
export const useGetMyFinishedCourses = (params?: { locale?: string }) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetFinishedCourses} = getImproveAPI();
	const effectiveParams: UserControllerGetFinishedCoursesParams = {
		locale: (params?.locale ?? DEFAULT_LOCALE) as UserControllerGetFinishedCoursesParams["locale"],
	};

	return useQuery({
		queryKey: [QUERY_KEYS.FINISHED_COURSES, effectiveParams],
		queryFn: async () => {
			const response = await userControllerGetFinishedCourses(effectiveParams, axiosConfig);
			return response.data;
		},
		staleTime: 5 * 60 * 1000 // 5 minutes - completed courses rarely change
	});
};
